import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BusinessIcon from "@mui/icons-material/Business";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AssignmentIcon from "@mui/icons-material/Assignment";

const getStatusColor = (status) => {
  const statusColors = {
    "Em aberto": "#f39c12",
    "Atendido": "#27ae60",
    "Cancelado": "#e74c3c",
    "Em andamento": "#3498db",
  };
  return statusColors[status] || "#95a5a6";
};

const getStatusFromSituacao = (situacao) => {
  const statusMap = {
    0: "Em aberto",
    1: "Atendido",
    2: "Cancelado",
    3: "Em andamento",
  };
  return statusMap[situacao] || "Em aberto";
};

export default function PurchaseOrderDetail({ open, onClose, purchaseOrder }) {
  if (!purchaseOrder || !purchaseOrder.originalData) {
    return null;
  }

  const data = purchaseOrder.originalData;
  const fornecedor = data.fornecedor || {};
  const endereco = fornecedor.endereco?.geral || {};
  const itens = data.itens || [];
  const transporte = data.transporte || {};
  const tributacao = data.tributacao || {};

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
        }
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "#ff9800",
                mr: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(255, 152, 0, 0.25)"
              }}
            >
              <AssignmentIcon sx={{ fontSize: "1.5rem", color: "white" }} />
            </Box>
            <Box>
              <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: "#424242" }}>
                Pedido #{data.numero}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                ID: {data.id} • Canal: {purchaseOrder.imported_channel}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Chip
              label={getStatusFromSituacao(data.situacao?.valor)}
              sx={{
                backgroundColor: getStatusColor(getStatusFromSituacao(data.situacao?.valor)),
                color: "white",
                fontWeight: 600,
                fontSize: "0.8rem",
                height: 32,
                borderRadius: 2,
              }}
            />
            <IconButton 
              onClick={onClose} 
              sx={{ 
                color: "text.secondary",
                "&:hover": { 
                  bgcolor: "rgba(0,0,0,0.04)",
                  transform: "scale(1.05)"
                },
                transition: "all 0.2s ease"
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Card sx={{ 
                height: "100%", 
                borderRadius: 3, 
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                border: "1px solid rgba(0,0,0,0.06)"
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2.5 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 2, 
                      bgcolor: "rgba(255, 152, 0, 0.1)",
                      mr: 1.5
                    }}>
                      <ReceiptIcon sx={{ color: "#ff9800", fontSize: "1.2rem" }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Informações Gerais</Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
                    <Box sx={{ 
                      flex: 1, 
                      minWidth: 140,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "rgba(33, 150, 243, 0.04)",
                      border: "1px solid rgba(33, 150, 243, 0.08)"
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Data
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CalendarMonthIcon sx={{ fontSize: 18, mr: 1, color: "#2196f3" }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.data}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      flex: 1, 
                      minWidth: 140,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "rgba(156, 39, 176, 0.04)",
                      border: "1px solid rgba(156, 39, 176, 0.08)"
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Data Prevista
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CalendarMonthIcon sx={{ fontSize: 18, mr: 1, color: "#9c27b0" }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.dataPrevista}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      flex: 1, 
                      minWidth: 140,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "rgba(255, 193, 7, 0.04)",
                      border: "1px solid rgba(255, 193, 7, 0.08)"
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Total Produtos
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <AttachMoneyIcon sx={{ fontSize: 18, mr: 1, color: "#ffc107" }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {parseFloat(data.totalProdutos || 0).toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      flex: 1, 
                      minWidth: 140,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "rgba(76, 175, 80, 0.06)",
                      border: "1px solid rgba(76, 175, 80, 0.12)"
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Total Geral
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <AttachMoneyIcon sx={{ fontSize: 18, mr: 1, color: "#4caf50" }} />
                        <Typography variant="body1" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                          {parseFloat(data.total || 0).toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  {data.ordemCompra && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Ordem de Compra
                      </Typography>
                      <Typography variant="body2">{data.ordemCompra}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Card sx={{ 
                height: "100%", 
                borderRadius: 3, 
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                border: "1px solid rgba(0,0,0,0.06)"
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2.5 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 2, 
                      bgcolor: "rgba(255, 152, 0, 0.1)",
                      mr: 1.5
                    }}>
                      <BusinessIcon sx={{ color: "#ff9800", fontSize: "1.2rem" }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Fornecedor</Typography>
                  </Box>
                  <Box sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    mb: 2.5,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "rgba(255, 152, 0, 0.04)",
                    border: "1px solid rgba(255, 152, 0, 0.08)"
                  }}>
                    <Avatar sx={{ 
                      bgcolor: "#ff9800", 
                      mr: 2, 
                      width: 48, 
                      height: 48,
                      fontSize: "1.2rem",
                      fontWeight: 600,
                      boxShadow: "0 4px 12px rgba(255, 152, 0, 0.2)"
                    }}>
                      {fornecedor.nome?.charAt(0) || "F"}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {fornecedor.fantasia || fornecedor.nome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {fornecedor.numeroDocumento}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body2">{fornecedor.email}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Telefone
                        </Typography>
                        <Typography variant="body2">{fornecedor.telefone}</Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Celular
                        </Typography>
                        <Typography variant="body2">{fornecedor.celular}</Typography>
                      </Box>
                    </Box>
                    {endereco.endereco && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Endereço
                        </Typography>
                        <Typography variant="body2">
                          {endereco.endereco}, {endereco.numero} - {endereco.bairro}
                        </Typography>
                        <Typography variant="body2">
                          {endereco.municipio}/{endereco.uf} - {endereco.cep}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {(data.observacoes || data.observacoesInternas) && (
            <Box>
              <Card sx={{ 
                borderRadius: 3, 
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                border: "1px solid rgba(0,0,0,0.06)"
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
                    Observações
                  </Typography>
                  {data.observacoes && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Observações Gerais
                      </Typography>
                      <Typography variant="body2">{data.observacoes}</Typography>
                    </Box>
                  )}
                  {data.observacoesInternas && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Observações Internas
                      </Typography>
                      <Typography variant="body2">{data.observacoesInternas}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Card sx={{ 
                height: "100%", 
                borderRadius: 3, 
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                border: "1px solid rgba(0,0,0,0.06)"
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2.5 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 2, 
                      bgcolor: "rgba(33, 150, 243, 0.1)",
                      mr: 1.5
                    }}>
                      <LocalShippingIcon sx={{ color: "#2196f3", fontSize: "1.2rem" }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Transporte</Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Typography variant="body2" color="text.secondary">
                        Frete
                      </Typography>
                      <Typography variant="body2">
                        {parseFloat(transporte.frete || 0).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Typography variant="body2" color="text.secondary">
                        Peso Bruto
                      </Typography>
                      <Typography variant="body2">{transporte.pesoBruto || 0} kg</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Typography variant="body2" color="text.secondary">
                        Volumes
                      </Typography>
                      <Typography variant="body2">{transporte.volumes || 0}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Typography variant="body2" color="text.secondary">
                        Frete por conta
                      </Typography>
                      <Typography variant="body2">
                        {transporte.fretePorConta === 0 ? "Emitente" : 
                         transporte.fretePorConta === 1 ? "Destinatário" : "Terceiros"}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Card sx={{ 
                height: "100%", 
                borderRadius: 3, 
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                border: "1px solid rgba(0,0,0,0.06)"
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2.5 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 2, 
                      bgcolor: "rgba(76, 175, 80, 0.1)",
                      mr: 1.5
                    }}>
                      <ReceiptIcon sx={{ color: "#4caf50", fontSize: "1.2rem" }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Tributação</Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Total ICMS
                      </Typography>
                      <Typography variant="body2">
                        {parseFloat(tributacao.totalICMS || 0).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Total IPI
                      </Typography>
                      <Typography variant="body2">
                        {parseFloat(tributacao.totalIPI || 0).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>

          <Box>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.06)"
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
                  Itens do Pedido ({itens.length})
                </Typography>
                <TableContainer component={Paper} sx={{ 
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)", 
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.06)"
                }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Produto</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>Qtd</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>Unidade</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Valor Unit.</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>IPI (%)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itens.map((item, index) => (
                        <TableRow 
                          key={index}
                          sx={{
                            "&:nth-of-type(odd)": { bgcolor: "rgba(0,0,0,0.01)" },
                            "&:hover": { bgcolor: "rgba(255, 152, 0, 0.04)" },
                            transition: "background-color 0.2s ease"
                          }}
                        >
                          <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {item.descricao}
                            </Typography>
                            {item.descricaoDetalhada && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                {item.descricaoDetalhada}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                            <Typography variant="body2" sx={{ 
                              fontFamily: "monospace", 
                              bgcolor: "rgba(0,0,0,0.04)",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: "0.8rem",
                              fontWeight: 500
                            }}>
                              {item.codigoFornecedor}
                            </Typography>
                            {item.produto?.codigo && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                {item.produto.codigo}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.quantidade}</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                            <Typography variant="body2">{item.unidade}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {parseFloat(item.valor || 0).toLocaleString("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                            <Typography variant="body2" sx={{ 
                              color: "#ff9800", 
                              fontWeight: 500,
                              bgcolor: "rgba(255, 152, 0, 0.08)",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1
                            }}>
                              {item.aliquotaIPI || 0}%
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 700,
                              color: "#2e7d32",
                              bgcolor: "rgba(76, 175, 80, 0.08)",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1
                            }}>
                              {(item.quantidade * parseFloat(item.valor || 0)).toLocaleString("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: "#f8f9fa", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          sx={{ 
            bgcolor: "#ff9800",
            borderRadius: 2,
            px: 4,
            py: 1.2,
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(255, 152, 0, 0.25)",
            "&:hover": { 
              bgcolor: "#f57c00",
              transform: "translateY(-1px)",
              boxShadow: "0 6px 16px rgba(255, 152, 0, 0.3)"
            },
            transition: "all 0.2s ease"
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}